import { DownloadConfigInfoType } from '@op/pages/download/types';
import {
  getCompetitorsAdCreativesList,
  getCompetitorsAdCreativesExtInfo,
  ITaggingInfo,
} from '@opm/services';

/**
 * 从 video_url 字段提取首个 URL
 */
function parseVideoUrl(raw: string): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed[0] || '';
  } catch {
    const match = raw.match(/\{?"?(https?:\/\/[^"}\s]+)"?\}?/);
    return match?.[1] || '';
  }
  return '';
}

/**
 * 将 ext_info 的标签/摘要合并到每条 list 记录上
 */
function mergeExtInfo(list: any[], extMap: Record<string, ITaggingInfo>) {
  return list.map((item: any) => {
    const url = parseVideoUrl(item.video_url);
    const info = url ? extMap[url] : undefined;

    // AI Tags
    const videoTagsEn = (info?.video_tags || [])
      .map(g => {
        const children = g.children.map(c => c.label_en).join(', ');
        return `${g.label_en} (${children})`;
      })
      .join(';\n\n');
    const videoTagsZh = (info?.video_tags || [])
      .map(g => {
        const children = g.children.map(c => c.label_zh).join(', ');
        return `${g.label_zh} (${children})`;
      })
      .join(';\n\n');

    // Content Summary
    const core = info?.content_summary?.find(
      s => s.label_en?.toLowerCase() === 'core content',
    );
    const hook = info?.content_summary?.find(
      s => s.label_en?.toLowerCase() === 'hook content',
    );

    return {
      ...item,
      video_tags_en: videoTagsEn || '',
      video_tags_zh: videoTagsZh || '',
      core_content_en: core?.summary_en || '',
      core_content_zh: core?.summary_zh || '',
      hook_content_en: hook?.summary_en || '',
      hook_content_zh: hook?.summary_zh || '',
    };
  });
}

export const MarketingCompetitorsAdCreativesDownload: DownloadConfigInfoType = {
  type: 'page',
  downloadFunc: async (params: any) => {
    const listRes = await getCompetitorsAdCreativesList({ data: params });
    const list = listRes?.list || [];
    if (list.length === 0) return listRes;

    // 提取 video_urls 和 game_ids 请求 ext_info
    const videoUrls: { video_url: string; game_id: string }[] = [];
    const gameIds = new Set<string>();
    list.forEach((item: any) => {
      const url = parseVideoUrl(item.video_url);
      if (url)
        videoUrls.push({
          video_url: url,
          game_id: item.game_id,
        });
    });

    const extMap: Record<string, ITaggingInfo> = {};
    if (videoUrls.length > 0 && gameIds.size > 0) {
      try {
        const extRes = await getCompetitorsAdCreativesExtInfo({
          data: { videos: videoUrls },
        });
        (extRes?.tagging_info || []).forEach(info => {
          if (info.video_url) extMap[info.video_url] = info;
        });
      } catch {
        // ext_info 失败不阻塞下载
      }
    }

    return {
      ...listRes,
      list: mergeExtInfo(list, extMap),
    };
  },
  columns: () => {
    return [
      { key: 'app_name', header: 'App Name' },
      { key: 'title', header: 'Title' },
      { key: 'body', header: 'Body' },
      { key: 'impression_number', header: 'Impressions' },
      { key: 'view_count', header: 'Views' },
      { key: 'like_count', header: 'Likes' },
      { key: 'comment_count', header: 'Comments' },
      { key: 'share_count', header: 'Shares' },
      { key: 'interaction_count', header: 'Interactions' },
      { key: 'days', header: 'Active Days' },
      { key: 'material_count', header: 'Ad Count' },
      { key: 'video_duration', header: 'Video Duration' },
      { key: 'first_seen', header: 'First Seen' },
      { key: 'last_seen', header: 'Last Seen' },
      { key: 'origin_url', header: 'URL' },
      { key: 'video_tags_en', header: 'AI Tags (English)' },
      { key: 'video_tags_zh', header: 'AI Tags (Chinese)' },
      { key: 'core_content_en', header: 'AI Core Content (English)' },
      { key: 'core_content_zh', header: 'AI Core Content (Chinese)' },
      { key: 'hook_content_en', header: 'AI Hook Analysis (English)' },
      { key: 'hook_content_zh', header: 'AI Hook Analysis (Chinese)' },
    ];
  },
};
